import { Modal, Box } from '@mui/material'
import { ReadTaskSummaryData } from '@common/config/api/client'
import { useGetSummaryReadTasks } from '../hooks/useGetSummaryReadTasks'
import { READ_TASK_SUMMARY_MOCK_DATA } from '../constants'
import ReaderSummaryLayout from '../components/summary/ReaderSummaryLayout'
import ReaderAdjudicationLayout from '../components/adjudication/ReaderAdjudicationLayout'
import { useCallback, useEffect, useRef, useState } from 'react'
import { launchDicomViewerForReader, getGcisViewerUrl } from '@common/utils/ohifUtils'
import Toast from '@common/components/Toast'
import { toast } from 'sonner'
import {
  getViewerLogicalName,
  focusOrOpenChildTab,
  registerTabCallback,
  unregisterTabCallback,
  closeAllTabs,
  broadCastAll,
} from '@common/utils/acrossTabsUtils'
import { useSigningContext } from '@features/reader_task_list_iam_view/pages/hooks/useSigningContext'
import EcrfLayout from '../components/adjudication/EcrfLayout'
import { queryClient } from '@common/config/api/queryClient'
import { MessageData } from 'across-tabs'
import { useIdleTimer } from 'react-idle-timer'
import { useSaveReaderActiveTime } from '../hooks/useSaveReaderActiveTime'
import { useMemo } from 'react'

export interface ReaderSummaryModalProps {
  open?: boolean
  onClose?: () => void
  taskId?: string
  studyId?: string
  initialModalStep?: string
  isRawDataEnabled?: boolean
}

interface DateRange {
  startTime: Date | number
  endTime: Date | number
}

export default function ReaderSummaryModal({
  open: propOpen,
  onClose: propOnClose,
  taskId: propTaskId,
  studyId,
  initialModalStep,
  isRawDataEnabled,
}: ReaderSummaryModalProps) {
  const { modalState, closeSigningModal } = useSigningContext()

  const isOpen = propOpen || (modalState?.isOpen ?? false)
  const taskId = propTaskId || (modalState?.taskId ?? '')

  const previousTaskIdRef = useRef<string>('')
  const IDLE_EVENTS = useMemo(() => {
    return ['visibilitychange']
  }, [])

  const [startTime, setStartTime] = useState<number | Date | null>(Date.now())
  const [endTime, setEndTime] = useState<number | Date | null>(Date.now())
  const { mutateAsync: saveReaderActionTime, isPending } = useSaveReaderActiveTime()
  const [isPaused, setIsPaused] = useState<boolean>(false)
  const isPausedRef = useRef(isPaused)
  const timeArray: (number | Date)[] = [] // to avoid multiple save calls with same time
  const queryKey = ['SummaryReadTasks', taskId]
  const { data, error, isLoading, refetch } = useGetSummaryReadTasks(queryKey, taskId)
  const { timepoints, read_task_summary_metadata }: ReadTaskSummaryData = data?.data ?? READ_TASK_SUMMARY_MOCK_DATA

  const defaultViewerIndex = (() => {
    if (isRawDataEnabled) return 2
    if (data?.data?.read_task_summary_metadata.type_of_gcis_viewer?.includes('bluerock')) return 3
    return 0
  })()

  useEffect(() => {
    if (taskId) {
      previousTaskIdRef.current = taskId
      setStartTime(Date.now())
      const bc = new BroadcastChannel('logout_channel')
      bc.onmessage = (event) => {
        if (event.data === 'logout') {
          closeAllTabs()
        }
      }
    }
  }, [taskId])

  useEffect(() => {
    isPausedRef.current = isPaused
  }, [isPaused])

  const { getLastActiveTime, start, pause } = useIdleTimer({
    timeout: 60000 * 3, // 3 minute of inactivity
    onIdle: () => {
      setEndTime(getLastActiveTime())
      pause()
      setIsPaused(true)
      broadCastAll('PARENT_INACTIVE')
      console.log('User is idle. Last active at:', getLastActiveTime())
    },
    onActive: () => {
      broadCastAll('PARENT_ACTIVE')
      setStartTime(Date.now())
      console.log('User is active again. Last active at:', Date.now())
    },
    debounce: 500,
  })

  useEffect(() => {
    let isCancelled = false
    const save = async () => {
      if (
        !startTime ||
        !endTime ||
        new Date(startTime).toISOString() == new Date(endTime).toISOString() ||
        isPending ||
        !previousTaskIdRef.current
      ) {
        return
      }

      try {
        await saveReaderTime(startTime, endTime)
      } catch (err) {
        if (!isCancelled) {
          console.error('Error saving reader time:', err)
        }
      }
    }

    save()

    return () => {
      isCancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTime])

  const saveReaderTime = async (startTime: Date | number | null, endTime: Date | number | null) => {
    if (startTime && endTime && new Date(startTime!) < new Date(endTime!) && !timeArray.includes(startTime)) {
      console.log(
        startTime,
        endTime,
        {
          start_time: new Date(startTime!).toISOString(),
          end_time: new Date(endTime!).toISOString(),
        },
        (new Date(endTime!).getTime() - new Date(startTime!).getTime()) / 1000,
        'inside save time',
      )
      saveReaderActionTime({
        readerActiveTimeData: {
          read_task_id: previousTaskIdRef.current,
          start_time: new Date(startTime).toISOString(),
          end_time: new Date(endTime).toISOString(),
        },
      }).then(() => {
        timeArray.push(startTime)
        setStartTime(endTime)
      })
    }
  }

  const handleSignAndSend = async (end_time: Date | number) => {
    setEndTime(end_time)
    await saveReaderTime(startTime, end_time)
  }

  const [isDisabled, setIsDisabled] = useState(true)
  const [launchedViewers, setLaunchedViewers] = useState<Set<string>>(new Set())

  // Generate a unique ID for this component instance for callback registration
  const callbackId = `reader-summary-modal-${taskId}`

  const handleClose = () => {
    if (data?.data?.read_task_summary_metadata.status !== 'COMPLETE') {
      // stop all processes
      saveReaderTime(startTime, Date.now())
      pause()
      removeEvents()
      setStartTime(null)
      setEndTime(null)
    }
    setIsDisabled(true)
    setLaunchedViewers(new Set())
    if (propOnClose) {
      propOnClose()
    }

    if (modalState) {
      closeSigningModal()
    }

    closeAllTabs()
    queryClient.invalidateQueries({ queryKey: ['ReadTasksList'] })
  }

  const restartIdleTimer = useCallback(() => {
    // start idle Timer when activity in parent
    if (isPausedRef.current && !document.hidden) {
      setStartTime(Date.now())
      start()
      broadCastAll('PARENT_ACTIVE')
      setIsPaused(false)
    }
  }, [start])

  const removeEvents = useCallback(() => {
    for (const event of IDLE_EVENTS) {
      window.removeEventListener(event, restartIdleTimer)
    }
  }, [IDLE_EVENTS, restartIdleTimer])

  const handleBeforeUnload = useCallback(() => {
    setEndTime(getLastActiveTime())
    removeEvents()
    broadCastAll('PARENT_INACTIVE')
    pause()
    closeAllTabs()
  }, [getLastActiveTime, pause, removeEvents])

  useEffect(() => {
    for (const event of IDLE_EVENTS) {
      window.addEventListener(event, restartIdleTimer)
    }
    // event to stop all processes when tab closed
    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        console.log('Tab is visible (active)')
        queryClient.invalidateQueries({ queryKey })
        refetch()
      }
    })
    return () => {
      // clean up
      setStartTime(null)
      setEndTime(null)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data?.data?.read_task_summary_metadata.status === 'COMPLETE') {
      // stop all processes when status changes
      setEndTime(getLastActiveTime())
      saveReaderTime(startTime, getLastActiveTime())
      pause()
      removeEvents()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data?.read_task_summary_metadata.status])

  useEffect(() => {
    // Register callback only once on mount with a specific ID
    registerTabCallback(callbackId, (d: MessageData) => {
      console.log(d.data)
      if (d.data === 'CHILD_INACTIVE' || d.type === 'CHILD_UNLOADED') {
        // start idle timer when child is inactive
        setStartTime(Date.now())
        start()
        setIsPaused(false)
      } else if (!isPausedRef.current) {
        // stop idle timer when any activity present at child
        setEndTime(getLastActiveTime())
        pause()
        setIsPaused(true)
      }

      if (d.type === 'CHILD_UNLOADED') {
        const dateRange = d.data as DateRange
        if (dateRange.startTime && dateRange.endTime) {
          saveReaderTime(dateRange.startTime, dateRange.endTime)
        }
      }

      if (
        data?.data?.read_task_summary_metadata.status !== 'COMPLETE' &&
        (d.type === 'SURVEY_COMPLETE' || d.type === 'GO_TO_GCIS_SUMMARY')
      ) {
        queryClient.invalidateQueries({ queryKey })
        refetch().then(() => {
          setIsDisabled(false)
        })
      } else if (d.type === 'MEASUREMENT_CHANGED' || d.type === 'ECRF_CHANGED') {
        setIsDisabled(true)
      }
    })

    // if (read_task_summary_metadata.type_of_ecrf === 'standalone') return

    if (isOpen && studyId && taskId && data?.data) {
      const hasValidData =
        timepoints &&
        timepoints.length > 0 &&
        timepoints[timepoints.length - 1].reader_submissions &&
        timepoints[timepoints.length - 1].reader_submissions.length > 0

      if (hasValidData && read_task_summary_metadata.read_task_type === 'ADJUDICATION') {
        const readerOneTaskId = timepoints[timepoints.length - 1].reader_submissions[0].read_task_id
        const adjudicationTaskId = taskId
        if (readerOneTaskId) {
          const viewerName = `dicomViewer-${readerOneTaskId}-${defaultViewerIndex}`
          if (!launchedViewers.has(viewerName)) {
            launchDicomViewerForReader(readerOneTaskId, studyId, defaultViewerIndex, adjudicationTaskId)
            setLaunchedViewers((prev) => new Set([...prev, viewerName]))
          }
        }
      } else {
        const viewerName = `dicomViewer-${taskId}-${defaultViewerIndex}`
        if (!launchedViewers.has(viewerName)) {
          launchDicomViewerForReader(taskId, studyId, defaultViewerIndex)
          setLaunchedViewers((prev) => new Set([...prev, viewerName]))
        }
      }
    } else if (isOpen && !(studyId && taskId)) {
      console.error('Missing required data for launching viewer.')
      toast(<Toast message="Cannot open viewer: Missing required information." variant="error" />)
    }

    // Clean up by unregistering the callback when component unmounts
    return () => {
      unregisterTabCallback(callbackId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callbackId,
    data?.data?.read_task_summary_metadata.status,
    queryKey,
    refetch,
    isOpen,
    read_task_summary_metadata.read_task_type,
    read_task_summary_metadata.type_of_ecrf,
    studyId,
    taskId,
    timepoints,
    launchedViewers,
  ])

  if (isLoading) {
    return <div>Loading...</div>
  }
  if (error) {
    return <div>Error: {error.message}</div>
  }

  if (!isOpen || !taskId) {
    return null
  }

  const onFocusChild = (index: number, forcedReaderTaskId?: string) => {
    const focusedReaderTaskId =
      forcedReaderTaskId || timepoints[timepoints.length - 1].reader_submissions[index]?.read_task_id
    let logicalName
    let viewerUrl
    if (read_task_summary_metadata.read_task_type === 'ADJUDICATION' && focusedReaderTaskId) {
      logicalName = getViewerLogicalName(focusedReaderTaskId, index)
      const adjudicationTaskId = taskId
      viewerUrl = getGcisViewerUrl(focusedReaderTaskId, studyId!, index, adjudicationTaskId)
    } else {
      logicalName = getViewerLogicalName(taskId, index)
      viewerUrl = getGcisViewerUrl(taskId, studyId!, index)
    }

    if (viewerUrl) {
      focusOrOpenChildTab(logicalName, viewerUrl)
    } else {
      console.error('[Component] Could not get viewer URL to focus/open tab.')
      toast(<Toast message="Could not get viewer URL to focus tab." variant="error" />)
    }
  }

  return (
    <Modal
      open={isOpen}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
      >
        {read_task_summary_metadata.type_of_ecrf === 'standalone' ? (
          <EcrfLayout
            timepoints={timepoints}
            readTaskId={taskId}
            isSignNSendDisabled={false}
            read_task_summary_metadata={read_task_summary_metadata}
            onClose={handleClose}
            onFocusChild={onFocusChild}
            readerSetEndTime={handleSignAndSend}
            pauseReaderActiveTime={pause}
            isRawDataEnabled={isRawDataEnabled}
          />
        ) : read_task_summary_metadata.read_task_type === 'ADJUDICATION' ? (
          <ReaderAdjudicationLayout
            timepoints={timepoints}
            readTaskId={taskId}
            read_task_summary_metadata={read_task_summary_metadata}
            onClose={handleClose}
            onFocusChild={onFocusChild}
            initialModalStep={initialModalStep}
            readerSetEndTime={handleSignAndSend}
            pauseReaderActiveTime={pause}
            isRawDataEnabled={isRawDataEnabled}
          />
        ) : (
          <ReaderSummaryLayout
            timepoints={timepoints}
            readTaskId={taskId}
            read_task_summary_metadata={read_task_summary_metadata}
            isSignNSendDisabled={isDisabled}
            onClose={handleClose}
            onFocusChild={onFocusChild}
            readerSetEndTime={handleSignAndSend}
            pauseReaderActiveTime={pause}
            isRawDataEnabled={isRawDataEnabled}
          />
        )}
      </Box>
    </Modal>
  )
}
